import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Banknote,
  CheckCircle2,
  CreditCard,
  HandCoins,
  QrCode,
} from "lucide-react";
import {
  Link,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  Card,
  InlineNotice,
  Money,
  PageHeader,
} from "../components/ui";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import { db } from "../lib/db";
import { applyLocalSale } from "../lib/local-operations";
import { flushSyncQueue } from "../lib/sync";
import { refreshFromServer } from "../lib/data";
import { todayBusinessDate } from "../lib/format";

type Method =
  | "CASH"
  | "BANK_TRANSFER"
  | "DEBT";

export function PaymentPage() {
  const cart = useCart();
  const auth = useAuth();
  const navigate = useNavigate();
  const customers = useLiveQuery(
    () =>
      db.customers
        .orderBy("name")
        .toArray(),
    [],
    [],
  );

  const lastClosing = useLiveQuery(
    async () =>
      (await db.meta.get("last-closing"))
        ?.value as
        | {
            businessDate?: string;
            state?: "OPEN" | "CLOSED" | "REOPENED";
          }
        | null
        | undefined,
    [],
  );

  const closedToday =
    String(lastClosing?.businessDate ?? "").slice(0, 10) ===
      todayBusinessDate() &&
    lastClosing?.state !== "REOPENED";

  const [method, setMethod] =
    useState<Method>("CASH");
  const [tendered, setTendered] =
    useState(cart.total);
  const [customerId, setCustomerId] =
    useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [successTotal, setSuccessTotal] =
    useState<number | null>(null);
  const [syncNotice, setSyncNotice] =
    useState("");
  const [reauthNeeded, setReauthNeeded] =
    useState(false);

  const bankConfirmationAvailable =
    auth.demo || navigator.onLine;

  if (
    cart.lines.length === 0 &&
    successTotal === null
  ) {
    return (
      <Navigate
        to="/sales"
        replace
      />
    );
  }

  async function complete() {
    setError("");
    setSyncNotice("");

    if (closedToday) {
      setError(
        "Ngày hôm nay đã đóng sổ. Chủ cửa hàng cần mở lại ngày trước khi ghi thêm giao dịch.",
      );
      return;
    }

    if (
      method === "BANK_TRANSFER" &&
      !bankConfirmationAvailable
    ) {
      setError(
        "Chuyển khoản cần kết nối mạng để xác nhận. Có thể chọn Tiền mặt hoặc Ghi nợ khi ngoại tuyến.",
      );
      return;
    }

    if (
      method === "CASH" &&
      tendered < cart.total
    ) {
      setError(
        "Số tiền khách đưa chưa đủ.",
      );
      return;
    }

    if (
      method === "DEBT" &&
      !customerId
    ) {
      setError(
        "Ghi nợ cần chọn khách hàng.",
      );
      return;
    }

    setBusy(true);
    const idempotencyKey =
      crypto.randomUUID();

    const payload = {
      idempotencyKey,
      lines: cart.lines.map((line) => ({
        productId: line.product.id,
        unitId: line.unit?.id ?? null,
        quantity: line.quantity,
      })),
      paymentMethod: method,
      customerId:
        method === "DEBT"
          ? customerId
          : null,
      allowNegativeStock: false,
    };

    try {
      let total = cart.total;

      if (auth.demo || !navigator.onLine) {
        total = await applyLocalSale(
          cart.lines,
          method,
          idempotencyKey,
          customerId || null,
          {
            demo: auth.demo,
            tenantId: auth.session?.tenant?.id,
            storeId: auth.session?.store?.id,
            allowNegativeStock: false,
          },
        );

        if (!auth.demo) {
          setSyncNotice(
            "Đơn đã lưu ngoại tuyến và sẽ tự đồng bộ khi có mạng.",
          );
        }
      } else {
        try {
          const saved = await api<{ total: number }>("/sales", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          total = Number(saved.total);
          await refreshFromServer();
        } catch (onlineError) {
          if (onlineError instanceof ApiError && onlineError.status < 500) {
            throw onlineError;
          }

          total = await applyLocalSale(
            cart.lines,
            method,
            idempotencyKey,
            customerId || null,
            {
              demo: false,
              tenantId: auth.session?.tenant?.id,
              storeId: auth.session?.store?.id,
              allowNegativeStock: false,
            },
          );

          const retry = await flushSyncQueue(
            auth.session?.tenant?.id,
            auth.session?.store?.id,
          );

          if (retry.unauthorized) {
            setReauthNeeded(true);
            setSyncNotice(
              "Đơn đã lưu an toàn trên thiết bị nhưng phiên đăng nhập đã hết. Đăng nhập lại để đồng bộ.",
            );
          } else if (retry.failed > 0 || retry.pendingRemaining > 0) {
            setSyncNotice(
              "Kết nối server gián đoạn. Đơn đang nằm trong hàng đợi đồng bộ; không cần nhập lại.",
            );
          } else {
            await refreshFromServer();
          }
        }
      }

      cart.clear();
      setSuccessTotal(total);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể hoàn tất thanh toán",
      );
    } finally {
      setBusy(false);
    }
  }

  if (successTotal !== null) {
    return (
      <div className="page-stack payment-success">
        <CheckCircle2 size={64} />
        <h1>Thanh toán thành công</h1>
        <Money
          value={successTotal}
          className="money-hero"
        />
        <p>
          {auth.demo
            ? "Giao dịch đã được lưu trong dữ liệu mẫu."
            : syncNotice ||
              "Giao dịch đã lưu và đồng bộ."}
        </p>
        <div className="success-actions">
          {reauthNeeded ? (
            <button
              className="btn btn-primary btn-lg"
              onClick={() => {
                auth.invalidateSession();
                navigate("/login", { replace: true });
              }}
            >
              Đăng nhập lại để đồng bộ
            </button>
          ) : null}
          <button
            className="btn btn-primary btn-lg"
            onClick={() =>
              navigate("/sales")
            }
          >
            Bán đơn mới
          </button>
          <Link
            className="btn btn-secondary btn-lg"
            to="/"
          >
            Về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Thanh toán"
        description="Kiểm tra đơn, chọn phương thức và xác nhận số tiền"
        back
      />

      {error ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      {closedToday ? (
        <InlineNotice
          tone="warning"
          title="Ngày kinh doanh đã khóa"
        >
          Không thể hoàn tất đơn này cho đến khi Chủ cửa hàng mở lại ngày.
        </InlineNotice>
      ) : null}

      <Card className="cart-card checkout-lines">
        <div className="cart-lines">
          {cart.lines.map((line) => (
            <div
              key={line.key}
              className="cart-line"
            >
              <div>
                <strong>
                  {line.product.name}
                </strong>
                <small>
                  {line.quantity}{" "}
                  {line.unit?.name ??
                    line.product.baseUnit}{" "}
                  ×{" "}
                  {line.unitPrice.toLocaleString(
                    "vi-VN",
                  )}{" "}
                  ₫
                </small>
              </div>
              <Money
                value={
                  line.unitPrice *
                  line.quantity
                }
              />
            </div>
          ))}
        </div>
      </Card>

      <Card className="payment-total">
        <span>Tổng thanh toán</span>
        <Money
          value={cart.total}
          className="money-hero"
        />
      </Card>

      <div className="payment-methods">
        <button
          className={
            method === "CASH"
              ? "selected"
              : ""
          }
          onClick={() =>
            setMethod("CASH")
          }
        >
          <Banknote />
          <strong>Tiền mặt</strong>
        </button>

        <button
          disabled={
            !bankConfirmationAvailable
          }
          className={
            method === "BANK_TRANSFER"
              ? "selected"
              : ""
          }
          onClick={() =>
            setMethod("BANK_TRANSFER")
          }
        >
          <QrCode />
          <strong>Chuyển khoản QR</strong>
        </button>

        <button
          className={
            method === "DEBT"
              ? "selected"
              : ""
          }
          onClick={() =>
            setMethod("DEBT")
          }
        >
          <HandCoins />
          <strong>Ghi nợ</strong>
        </button>
      </div>

      {method === "CASH" ? (
        <Card className="form-stack">
          <label>
            Khách đưa
            <input
              className="currency-field"
              inputMode="numeric"
              value={tendered}
              onChange={(event) =>
                setTendered(
                  Number(
                    event.target.value.replace(
                      /\D/g,
                      "",
                    ),
                  ) || 0,
                )
              }
            />
          </label>

          <div className="quick-cash">
            {[
              cart.total,
              Math.ceil(
                cart.total / 50_000,
              ) * 50_000,
              500_000,
            ]
              .filter(
                (value, index, all) =>
                  value >= cart.total &&
                  all.indexOf(value) ===
                    index,
              )
              .map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setTendered(value)
                  }
                >
                  {value.toLocaleString(
                    "vi-VN",
                  )}{" "}
                  ₫
                </button>
              ))}
          </div>

          <div className="change-row">
            <span>Tiền thừa</span>
            <Money
              value={Math.max(
                0,
                tendered - cart.total,
              )}
            />
          </div>
        </Card>
      ) : null}

      {method === "BANK_TRANSFER" ? (
        <Card className="qr-placeholder">
          <QrCode size={72} />
          <strong>
            Chuyển khoản đúng số tiền
          </strong>
          <Money
            value={cart.total}
            className="money-large"
          />
          <p>
            Tích hợp VietQR/bank provider được
            tách thành adapter. Hiện chỉ cho
            xác nhận thủ công sau khi người bán
            thực sự kiểm tra tiền đã vào.
          </p>
          <InlineNotice
            tone="info"
            title="Không tự suy đoán trạng thái ngân hàng"
          >
            Chỉ bấm xác nhận sau khi đã kiểm
            tra giao dịch thực tế.
          </InlineNotice>
        </Card>
      ) : null}

      {method === "DEBT" ? (
        <Card className="form-stack">
          <label>
            Khách hàng
            <select
              value={customerId}
              onChange={(event) =>
                setCustomerId(
                  event.target.value,
                )
              }
            >
              <option value="">
                Chọn khách hàng
              </option>
              {customers.map(
                (customer) => (
                  <option
                    key={customer.id}
                    value={customer.id}
                  >
                    {customer.name}
                    {customer.phone
                      ? ` — ${customer.phone}`
                      : ""}
                  </option>
                ),
              )}
            </select>
          </label>

          {customers.length === 0 ? (
            <p className="helper">
              Chưa có khách hàng. Thêm khách
              hàng trước khi ghi nợ.
            </p>
          ) : null}
        </Card>
      ) : null}

      <button
        className="btn btn-primary btn-lg sticky-action"
        disabled={
          busy ||
          closedToday ||
          (method === "DEBT" &&
            !customerId) ||
          (method ===
            "BANK_TRANSFER" &&
            !bankConfirmationAvailable)
        }
        onClick={() => void complete()}
      >
        {method === "BANK_TRANSFER" ? (
          <CreditCard size={20} />
        ) : (
          <CheckCircle2 size={20} />
        )}
        {busy
          ? "Đang lưu..."
          : method === "BANK_TRANSFER"
            ? "Tôi đã nhận được tiền"
            : "Hoàn tất thanh toán"}
      </button>
    </div>
  );
}
